import { RadioGroup, RadioItem } from '@/src/components/Antd';
import CommonHeader from '@/src/components/CommonHeader';
import NoPermission from '@/src/components/NoPermission';
import { getSalesChannel } from '@/src/request/salesChannel/salesChannel';
import { SalesChannelModeAccessControlHOC } from 'src/constrains/SalesChannelModeAccessControl';
import {
  getInventorySetting,
  postInventorySetting,
} from '@/src/request/salesChannel/setting/inventory';
import {
  isUserValid,
  salesChannelResourceNameGenerator,
} from '@/src/tools/getUserInfo';
import {
  EnumCategory,
  EnumOperation,
  EnumSubCategory,
  EPermissionSource,
} from '@/src/types/common/resources';
import { useRequest } from 'ahooks';
import { Form, Input, Spin } from 'antd';
import React, { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router';
import { breadcrumbData } from './config';
import styles from './style.module.less';
import { useI18n } from 'src/hooks/useRouterChange';
import { i18nGet } from 'src/tools/tools';

const permissions = (uuid: string) => {
  const view_inventory_setting = isUserValid({
    source: EPermissionSource.salesChannel,
    resourcesGroup: [
      salesChannelResourceNameGenerator({
        category: EnumCategory.SalesChannelSettings,
        subCategory: EnumSubCategory.ChannelInventory,
        operation: EnumOperation.view_inventory_setting,
        uuid,
      }),
    ],
  });
  const edit_inventory_setting = isUserValid({
    source: EPermissionSource.salesChannel,
    resourcesGroup: [
      salesChannelResourceNameGenerator({
        category: EnumCategory.SalesChannelSettings,
        subCategory: EnumSubCategory.ChannelInventory,
        operation: EnumOperation.edit_inventory_setting,
        uuid,
      }),
    ],
  });

  return {
    view_inventory_setting,
    edit_inventory_setting,
  };
};

const Inventory = (props) => {
  const i18n = useI18n();
  const getI18n = (str) => i18nGet(i18n, str);
  const params: any = useParams();
  const { uuid } = params;
  const { view_inventory_setting, edit_inventory_setting } = permissions(uuid);
  const [form] = Form.useForm();
  const [onSaveDisable, setOnSaveDisable] = React.useState<boolean>(true);
  const [radioValue, setRadioValue] = useState('Off');
  const { data, loading } = useRequest(() => getInventorySetting(uuid), {
    ready: !!view_inventory_setting,
  });
  const { data: salesChannelData } = useRequest(() => getSalesChannel(uuid), {
    ready: !!view_inventory_setting,
  });
  const { mode = '', threshold = 5 } = data || {};
  const { name = '' } = salesChannelData || {};
  const { run: _postInventorySetting } = useRequest(
    (submitData) => postInventorySetting(uuid, submitData),
    {
      manual: true,
      onSuccess: () => {
        history.push(`/salesChannel/settings/${uuid}`);
      },
    },
  );
  const onValuesChange = (e) => {
    if (onSaveDisable) {
      setOnSaveDisable(!onSaveDisable);
    }
  };
  const onFormSubmit = () => {
    form.validateFields().then((res) => {
      _postInventorySetting({
        mode: res.mode,
        threshold: res.threshold,
      });
    });
  };
  const history = useHistory();
  const goBackToList = () => {
    history.push(`/salesChannel/settings/${uuid}`);
  };
  useEffect(() => {
    if (!loading) {
      const defaultForm = {
        mode: mode,
        threshold: threshold || 5,
      };
      setRadioValue(mode);
      form.setFieldsValue(defaultForm);
    }
  }, [loading]);

  return view_inventory_setting ? (
    <div className={styles.container}>
      <Spin spinning={loading}>
        <Form
          layout={'vertical'}
          form={form}
          className={styles.editForm}
          onValuesChange={onValuesChange}>
          <CommonHeader
            breadcrumbData={breadcrumbData(name, uuid, { getI18n })}
            onSaveDisable={onSaveDisable || !edit_inventory_setting}
            onSubmit={onFormSubmit}
            onDiscard={goBackToList}
            onGoBackClick={goBackToList}
          />
          <div className={styles.itemBlock}>
            <div className={styles.header}>
              {getI18n('salesChannelInventory.index.availableForSaleQuantity')}
            </div>
            <div className={styles.content}>
              <div>
                <div className={styles.title}>
                  {getI18n(
                    'salesChannelInventory.index.availableForSaleQuantityTitle',
                  )}
                </div>
                <div>
                  <Form.Item name={'mode'}>
                    <RadioGroup
                      value={radioValue}
                      disabled={!edit_inventory_setting}
                      className={styles.radioGroup}
                      onChange={(e) => {
                        setRadioValue(e.target.value);
                      }}>
                      <RadioItem className={styles.radioItem} value={'Off'}>
                        {getI18n('salesChannelInventory.index.off')}
                      </RadioItem>
                      <RadioItem
                        className={styles.radioItem}
                        value={'AlwaysOn'}>
                        {getI18n('salesChannelInventory.index.alwayson')}
                      </RadioItem>
                      <RadioItem
                        className={`${styles.radioItem}`}
                        value={'Threshold'}>
                        {getI18n('salesChannelInventory.index.threshold')}
                      </RadioItem>
                    </RadioGroup>
                  </Form.Item>
                </div>
                {radioValue == 'Threshold' && (
                  <div className={styles.thresholdInput}>
                    {getI18n('salesChannelInventory.index.lowerthan')}
                    <Form.Item
                      name={'threshold'}
                      className={styles.thresholdItem}
                      rules={[
                        {
                          validator: async (_, value) => {
                            if (value) {
                              if (value < 2) {
                                throw new Error(
                                  getI18n(
                                    'salesChannelInventory.index.thresholdValidError1',
                                  ),
                                );
                              } else if (value > 1000000) {
                                throw new Error(
                                  getI18n(
                                    'salesChannelInventory.index.thresholdValidError2',
                                  ),
                                );
                              }
                            }
                          },
                        },
                      ]}>
                      <Input
                        className={styles.input}
                        type='number'
                        size='large'
                        min={2}
                        max={1000000}
                        disabled={!edit_inventory_setting}
                      />
                    </Form.Item>
                    {getI18n('salesChannelInventory.index.items')}
                  </div>
                )}
              </div>
            </div>
          </div>
        </Form>
      </Spin>
    </div>
  ) : (
    <NoPermission />
  );
};
export default SalesChannelModeAccessControlHOC(Inventory);
